import * as ko from "knockout";
import * as $ from "../jquery";
import { pli } from "./pli";


declare module "./pli" {
    /**
     * Extend base Pli configuration with call-specific settings.
     */
    interface PliConfig {
        /**
         * Extra HTTP headers to include with every Pli call.
         */
        defaultHeaders: { [key: string]: any; };

        /**
         * Status code to use for "unauthenticated" responses.
         * This should only be changed for applications not using a
         * modern HTTP pipeline (e.g., PLI v4.x, set to 403).
         * @default 401
         */
        unauthenticatedStatusCode?: number;
    }
}


/**
 * Settings for "PliCallSignInHandler" controlling its behaviour.
 */
export interface PliCallSignInHandlerSettings {
    /**
     * When specified, sign-in handler should keep its UI visible until "true" is set (even after sign-in completes).
     * When sign-in handler is used during form initialization, it "feels better"
     * if the sign-in screen only goes away after the form initialization completes.
     * Consider: 1. Load call is made (while a spinner is spinning);
     * 2. Server responds with 401: Unauthorized, 3. Sign-in handler is
     * invoked, showing sign-in UI (hiding the spinner from step 1);
     * 4. After user successfully authenticates, sign-in observable
     * (return value from PliCallSignInHandler) is set to "true" and
     * normally sign-in UI would be hidden; however, if "deferClosingUntil"
     * is specified, sign-in UI would stay visible (with a signing-on spinner);
     * 5. Load call is retried; 6. Viewmodel is initialized with result of
     * load call; "deferClosingUntil" observable can now be set to "true",
     * at which point the fully initialized form will appear.
     */
    deferClosingUntil?: ko.Observable<boolean>;

    /**
     * Information received from the server in the "401: Unauthenticated" response.
     * Can contain additional information for the sign-in-handler (e.g. server messages).
     */
    serverData?: any;
}


/**
 * Signature for sign-in handler (that is used when Pli call fails with "401 Unauthenticated" status).
 */
export interface PliCallSignInHandler {
    /**
     * Sign-in handler.
     *
     * @returns {ko.Observable<boolean>}
     *     Must be null or a valid observable notifying call function when sign-in is complete (or cancelled).
     *     This observable must be treated as read-only (callers are not allowed
     *     to set the value, only sign-in handler sets it) and must be set to
     *     "null" (indicates asynchronous completion) or a boolean value
     *     (indicates synchronous completion) when returned from sign in handler.
     *
     *     Sign in handler is then expected to set a boolean value
     *     to indicate completion (non-boolean values are ignored).
     *     Setting "true" indicates sign in success and will retry
     *     the call that triggered the sign in handler. Setting
     *     "false" will fail the call (same as returning "null"
     *     instead of an observable from the sign in handler).
     */
    (settings?: PliCallSignInHandlerSettings): ko.Observable<boolean>;
}


/**
 * Configuration settings for Pli calls.
 */
export interface PliCallSettings<TRequestData, TOwner> {
    /**
     * Optional callback that implements custom sign-in mechanism.
     *
     * Will be used when Pli call fails with 401 Unauthenticated status.
     *
     * If no function is specified, uses the default sign-in handler.
     * The function is expected to return subscribable that will notify
     * us with "true" if sign-in was successful (the unauthenticated call
     * will then be re-tried), or "false" (give up, i.e. fail the call).
     */
    customSignIn?: PliCallSignInHandler;

    /**
     * Data to send to the server as part of Pli call.
     *
     * When not specified, defaults to "null".
     */
    data?: TRequestData;

    /**
     * Specify observable to pass to "PliCallSignInHandler" when "401: Unauthorized" is returned from server.
     *
     * See comments for "deferClosingUntil" in "PliCallSignInHandler" for more information.
     * When unset, sign-in handler will close its UI as soon as sign-in completes (normal behaviour).
     */
    deferSignInUiClosingUntil?: ko.Observable<boolean>;

    /**
     * Extra HTTP headers to include with the Pli call.
     */
    headers?: { [key: string]: any; };

    /**
     * @deprecated OBSOLETE, DO NOT USE. When truthy, skips custom handling of forbidden requests (403 status code)
     * (will fallback to default error handling instead).
     *
     * When not specified, defaults to falsy (use custom 403 handling).
     *
     * Use "ignoreUnauthenticated" instead.
     */
    ignoreForbidden?: boolean;

    /**
     * When truthy, passes 401 responses to error handler instead of using sign-in handler.
     *
     * When not specified, defaults to falsy (use sign-in handler).
     */
    ignoreUnauthenticated?: boolean;

    /**
     * Data, already encoded as a JSON string, to send to the server as part of Pli call.
     *
     * When not specified, "data" will be used instead.
     * When specified, "data" will be ignored.
     */
    jsonData?: string;

    /**
     * Name of the Pli call to invoke. Required, must not be falsy value.
     */
    name: string;

    /**
     * Arbitrary data (that makes sense to the caller).
     *
     * When specified, will be passed to response handler (success and failure).
     * Typically used to include reference to the object
     * making the call so that no closure or bind is needed
     * in response handler.
     */
    owner?: TOwner;

    /**
     * Force request to operate in synchronous mode. Deprecated, DO NOT USE
     * (blocks browser, including UI, until server call finishes
     * and response is received).
     *
     * When not specified, defaults to false (async call).
     */
    sync?: boolean;
}


/**
 * Shape of response data in failure response handler.
 */
export interface PliCallErrorData {
    /**
     * List of corrective actions that explain what happened in user-friendly, what-should-I-do-next language.
     */
    correctiveActions?: string[];

    /**
     * Detailed error message (including stack trace). Only enabled in debug build.
     */
    details?: string;

    /**
     * Error message sent by Pli server (or XHR provider message explaining why Pli server couldn't be reached).
     */
    message: string;

    /**
     * URL to redirect current browser window/tab/frame to. Only valid when response HTTP status is "412: Precondition Failed".
     */
    redirectLocation?: string;

    /**
     * Type of the server-side exception that was thrown.
     */
    type?: string;

    /**
     * List of validation errors (mapping path-to-field to validation error message).
     */
    validationErrors?: { [pathToField: string]: string };
}


/**
 * Response object passed to success/error response handlers.
 */
export interface PliCallResponse<TResponseData, TOwner> {
    /**
     * Error reported by XHR provider.
     */
    clientErrorThrown?: any;

    /**
     * Human-readable response status as reported by XHR provider.
     */
    clientStatus: string;

    /**
     * XHR object that was used to talk to the Pli server
     * (can be used to extract additional information from HTTP headers, etc).
     */
    clientXhr: JQueryXHR;

    /**
     * A flag that indicates whether the request was successful (false) or not (true).
     */
    isError: boolean;

    /**
     * Caller-specific data that was specified in "callSettings.owner".
     */
    owner: TOwner;

    /**
     * Result of the call (isError === false) or error information (isError === true).
     */
    result: TResponseData | PliCallErrorData;
}


/**
 * Promise returned from Pli call function.
 */
export interface PliCallPromise<TResponseData, TOwner> extends JQueryPromise<PliCallResponse<TResponseData, TOwner>> {
    /**
     * XHR object used-to-talk-to/still-talking-to Pli server.
     * If still active, can be used to abort the call.
     */
    xhr: JQueryXHR;
}


/**
 * Current function to call when "pliCall" receives "401 Unauthorized" response.
 *
 * Set to null by default (no action, fail the call).
 * Can be set for all calls using "setDefaultSignIn" exported function.
 * Can be overridden for one call in "settings.customSignIn".
 */
let defaultSignIn: PliCallSignInHandler = null;


/**
 * Sets default sign-in handler to the specified value (must be a function or null).
 *
 * @returns {PliCallSignInHandler}
 *     Value of parameter "handler" (allows chaining).
 */
export function setDefaultSignInHandler(handler: PliCallSignInHandler): PliCallSignInHandler {
    if (handler !== null && typeof handler !== "function") { throw new Error("Default sign-in handler must be a function or null."); }
    defaultSignIn = handler;
    return handler;
}


/**
 * Handles sign-in notifications (called from "pliCallFailed"-s subscription).
 *
 * We expect subscribable returned by sign-in handler to notify us with
 * boolean value when sign-in process is complete (new authentication
 * cookie has been issued) or cancelled (user gave up).
 *
 * This handler will retry the Pli call that triggered the sign-in handler
 * (when the subscribable notification value, "signInSucceeded", is "true")
 * or fail the call (when "signInSucceeded" is "false"). Any other value
 * will be ignored (e.g. if the source observable is reset to "null" or "undefined").
 */
function pliCallSignInDone<TRequestData, TOwner, TResponseData>(
    signInSucceeded: boolean, // value that sign-in subscribable notified us with
    signInSubscription: ko.subscription<boolean>,
    errorResponse: PliCallResponse<TResponseData, TOwner>,
    settings: PliCallSettings<TRequestData, TOwner>,
    xhrSettings: JQueryAjaxSettings,
    callPromise: PliCallPromise<TResponseData, TOwner>,
    callPromiseController: JQueryDeferred<PliCallResponse<TResponseData, TOwner>>): boolean
{
    // ignore non-boolean values
    if (typeof signInSucceeded !== "boolean") {
        return false;
    }

    // subscription is not needed anymore (if any)
    if (signInSubscription) {
        signInSubscription.dispose();
    }

    // retry or fail
    if (signInSucceeded) {
        pliCallExecute(settings, xhrSettings, callPromise, callPromiseController); // retry the call
    } else {
        callPromiseController.reject(errorResponse);
    }

    return true;
}


/**
 * Handles failed JQuery XHR response for a Pli call (called from "pliCallExecute"-s fail handler).
 *
 * Special handling (based on HTTP status):
 * - "412: Precondition Failed" will redirect the current window/tab/iframe to the URL specified
 * - "401: Unauthorized" will use sign-in handler to give user a chance to re-authenticate (then retry the call)
 */
function pliCallFailed<TRequestData, TOwner, TResponseData>(
    xhr: JQueryXHR,
    textStatus: string,
    errorThrown: any,
    settings: PliCallSettings<TRequestData, TOwner>,
    xhrSettings: JQueryAjaxSettings,
    callPromise: PliCallPromise<TResponseData, TOwner>,
    callPromiseController: JQueryDeferred<PliCallResponse<TResponseData, TOwner>>): void
{
    // make sure result is always an object
    const result: PliCallErrorData = (typeof xhr.responseJSON !== "object" || xhr.responseJSON === null
        ? { value: xhr.responseJSON }
        : xhr.responseJSON);

    // "412: Precondition Failed" + a valid response object => redirect
    if (xhr.status === 412 && result.redirectLocation) {
        location.replace(result.redirectLocation);
        return;
    }

    // create an object compatible with "PliCallResponse" interface
    if (!result.message) {
        result.message = "XHR call failed (status: \"".concat(textStatus).concat("\", error: \"").concat(errorThrown).concat("\")");
    }
    const errorResponse: PliCallResponse<TResponseData, TOwner> = {
        clientErrorThrown: errorThrown,
        clientStatus: textStatus,
        clientXhr: xhr,
        isError: true,
        owner: settings.owner,
        result: result
    };

    // "401: Unauthorized" (use sign-in handler, if any)
    const unauthenticatedStatusCode = pli.config.unauthenticatedStatusCode || 401;
    if (xhr.status === unauthenticatedStatusCode && !settings.ignoreUnauthenticated) {
        const signInHandler = settings.customSignIn || defaultSignIn;
        if (signInHandler) {
            // trigger sign-in handler
            const signInDone = signInHandler({ deferClosingUntil: settings.deferSignInUiClosingUntil, serverData: result });

            // wait for sign-in handler to be done
            if (ko.isSubscribable(signInDone)) { // handler indicated it will be done later (when the observable it returned is set)
                const signInDoneValue = signInDone.peek();
                if (typeof(signInDoneValue) === "boolean") { // handler finished synchronously
                    pliCallSignInDone(signInDoneValue, /* signInSubscription */ null, errorResponse, settings, xhrSettings, callPromise, callPromiseController);
                }
                else if (signInDoneValue === null) { // handler will finish asynchronously, enforce initial "null" value
                    const signInDoneSubscription: ko.subscription<boolean> = signInDone.subscribe(signInSucceeded => // wait for handler to be done
                        pliCallSignInDone(signInSucceeded, signInDoneSubscription, errorResponse, settings, xhrSettings, callPromise, callPromiseController));
                }
                else { // handler is confused
                    throw new Error("Sign-in handler was expected to return an observable set to \"null\" or a boolean value, was: " + JSON.stringify(signInDoneValue));
                }
            }
            else if (signInDone === null) { // handler gave up (fail the call)
                pliCallSignInDone(/* signInSucceeded */ false, /* signInSubscription */ null, errorResponse, settings, xhrSettings, callPromise, callPromiseController);
            }
            else { // handler is confused
                throw new Error("Sign-in handler was expected to return null or an observable");
            }

            // we are done processing the current call; either we have
            // already failed it (if sign in handler gave up on us)
            // or we retried/failed it (if sign in handler completed
            // synchronously) or we will retry/fail the call in
            // the future when sign in handler signals its "done" observable
            return;
        }
    }

    // notify promise observers
    callPromiseController.reject(errorResponse);
}


/**
 * Handles successful JQuery XHR response for a Pli call (called from "pliCallExecute"-s done handler).
 *
 * This function builds an object compatible with "PliCallResponse" interface
 * and resolves the specified promise (notifying all promise observers).
 */
function pliCallSucceeded<TResponseData, TOwner>(
    data: any,
    textStatus: string,
    xhr: JQueryXHR,
    owner: TOwner,
    callPromiseController: JQueryDeferred<PliCallResponse<TResponseData, TOwner>>): void
{
    callPromiseController.resolve({
        clientStatus: textStatus,
        clientXhr: xhr,
        isError: false,
        owner: owner,
        result: data
    });
}


/**
 * Invokes the specified call as JQuery XHR request + setup response handlers (called by "pliCall" and "pliCallSignInDone").
 *
 * Recursive entry point for Pli calls. The same call can be retried
 * multiple times by calling this function with the same parameters
 * (for example when retrying a call after sign-in authentication).
 */
function pliCallExecute<TRequestData, TOwner, TResponseData>(
    settings: PliCallSettings<TRequestData, TOwner>,
    xhrSettings: JQueryAjaxSettings,
    callPromise: PliCallPromise<TResponseData, TOwner>,
    callPromiseController: JQueryDeferred<PliCallResponse<TResponseData, TOwner>>): void
{
    callPromise.xhr = $.ajax(xhrSettings.url, xhrSettings);

    const initialXhrState = callPromise.xhr.state(); // http://api.jquery.com/deferred.state/
    if (initialXhrState !== "rejected") {
        callPromise.xhr.done((data: any, textStatus: string, xhr: JQueryXHR) =>
            pliCallSucceeded(data, textStatus, xhr, settings.owner, callPromiseController));
    }

    if (initialXhrState !== "resolved") {
        callPromise.xhr.fail((xhr: JQueryXHR, textStatus: string, errorThrown: any) =>
            pliCallFailed(xhr, textStatus, errorThrown, settings, xhrSettings, callPromise, callPromiseController));
    }
}


/**
 * Initiates an HTTP (Ajax) request that executes a function on PLI server.
 * Uses JSON for data transport to server (request) and from server (response).
 *
 * @param {PliCallSettings<TRequestData, TOwner>} settings
 *     Defines name, data and other parameters of the PLI call.
 *
 * @returns {PliCallPromise<TResponseData, TOwner>}
 *     jQuery promise object that will be resolved/rejected when the call is finished
 *     (http://api.jquery.com/deferred.promise/).
 *
 *     Returned object also includes a property 'xhr' that references
 *     the underlying jQuery.ajax jqXHR request/promise object
 *     (http://api.jquery.com/jQuery.ajax/#jqXHR) that can be used to abort the PLI call.
 */
export default function pliCall<TRequestData, TOwner, TResponseData>(settings: PliCallSettings<TRequestData, TOwner>): PliCallPromise<TResponseData, TOwner> { // tslint:disable-line: no-default-export // TODO: remove in v8
    // validate settings object
    if (typeof settings !== "object" || typeof settings.name !== "string") {
        throw new Error("pliCall: Expected \"PliCallSettings.name\" to contain PLI call name.");
    }
    if (settings.ignoreForbidden !== undefined) {
        throw new Error("pliCall: \"PliCallSettings.ignoreForbidden\" is not supported anymore, use \"ignoreUnauthenticated\" instead.");
    }
    if ((pliCall as any).logOn !== undefined) {
        throw new Error("pliCall: \"pliCall.logOn\" is not supported anymore, use \"PliCallSettings.customSignIn\" instead.");
    }

    // build XHR settings
    const requestJsonData = (typeof settings.jsonData === "string" // encode data as JSON string (if needed)
        ? settings.jsonData
        : JSON.stringify(settings.data === undefined ? null : settings.data));
    const headers = settings.headers || pli.config.defaultHeaders || {};
    if (typeof location !== "undefined") {
        headers["X-Pli-App-Url"] = location.href;
    }
    const xhrSettings: JQueryAjaxSettings = { // settings
        url: pli.resolveUrl("~/Pli/Call/").concat(encodeURIComponent(settings.name)),
        type: "POST",
        async: !settings.sync, // any falsey value will trigger async call
        contentType: "application/json; charset=utf-8",
        dataType: "json",
        headers: headers,
        data: requestJsonData
    };

    // execute the call
    const callPromiseController = $.Deferred<PliCallResponse<TResponseData, TOwner>>(); // used in success/failure handlers to resolve/reject "callPromise"
    const callPromise = callPromiseController.promise() as PliCallPromise<TResponseData, TOwner>;
    pliCallExecute(settings, xhrSettings, callPromise, callPromiseController); // mutates "callPromise", setting "xhr" property
    return callPromise;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/core/call.ts